import React, {RefObject, ReactNode, createContext, useCallback, useContext, useMemo} from 'react';
import {ScrollView} from 'react-native';

export interface ScrollContextShape {
  readonly scrollRef: RefObject<ScrollView | null>;
  readonly scrollTo: (y: number) => void;
}

const ScrollContext = createContext<ScrollContextShape>({} as ScrollContextShape);

interface ScrollProviderProps {
  readonly scrollRef: RefObject<ScrollView | null>;
  readonly children: ReactNode;
}

const useScrollContext = (): ScrollContextShape => {
  const context = useContext(ScrollContext);

  if (context === undefined) {
    throw new Error('useScrollContext must be used within a ScrollProvider');
  }

  return context;
};

const ScrollProvider: React.FC<ScrollProviderProps> = ({scrollRef, children}) => {
  const scrollTo = useCallback((y: number) => scrollRef.current?.scrollTo({y}), [scrollRef]);

  const value = useMemo(() => ({scrollRef, scrollTo}), [scrollRef, scrollTo]);

  return <ScrollContext.Provider value={value}>{children}</ScrollContext.Provider>;
};

export {ScrollProvider, useScrollContext};
